/**
 * Skill Catalog
 * Static definitions for all combat skills.
 * Skills are granted to participants via tags (players) or NPC tag mapping.
 */

import type { SkillDefinition } from './types';
import { resolveSkills } from './tags';

// ============================================================================
// SKILL CATALOG
// ============================================================================

export const SKILL_CATALOG: Record<string, SkillDefinition> = {
  shield_bash: {
    id: 'shield_bash',
    name: 'Shield Bash',
    description: 'Slam your shield into an enemy, dealing damage and pushing them back',
    category: 'offensive',
    stat: 'brawn',
    targeting: 'single_enemy',
    range: 'melee',
    apCost: 2,
    resolveCost: 1,
    cooldown: { type: 'per_round', maxUses: 1 },
    effects: [
      { type: 'damage', baseDamage: 2 },
      { type: 'force_move', direction: 'retreat' },
    ],
    keywords: ['melee', 'physical', 'push'],
    requiresRoll: true,
    generatesThreat: true,
    threatProperties: { canBeBlocked: true, canBeDodged: true, canBeParried: false },
  },

  whirlwind: {
    id: 'whirlwind',
    name: 'Whirlwind',
    description: 'A devastating spinning attack that strikes all nearby enemies',
    category: 'offensive',
    stat: 'brawn',
    targeting: 'all_enemies',
    range: 'close',
    apCost: 3,
    resolveCost: 2,
    cooldown: { type: 'per_combat', maxUses: 1 },
    effects: [
      { type: 'damage', baseDamage: 3 },
    ],
    keywords: ['melee', 'physical', 'aoe'],
    requiresRoll: true,
    generatesThreat: true,
    threatProperties: { canBeBlocked: true, canBeDodged: true, canBeParried: false },
  },

  shield_wall: {
    id: 'shield_wall',
    name: 'Shield Wall',
    description: 'Raise your shield to absorb incoming attacks for the next two rounds',
    category: 'defensive',
    stat: 'brawn',
    targeting: 'self',
    range: 'self',
    apCost: 1,
    resolveCost: 2,
    cooldown: { type: 'per_combat', maxUses: 1 },
    effects: [
      { type: 'apply_condition', condition: 'shielded', duration: 2, target: 'self' },
    ],
    keywords: ['defensive', 'shield'],
    requiresRoll: false,
    generatesThreat: false,
  },

  second_wind: {
    id: 'second_wind',
    name: 'Second Wind',
    description: 'Dig deep and recover some vitals through sheer determination',
    category: 'defensive',
    stat: 'brawn',
    targeting: 'self',
    range: 'self',
    apCost: 2,
    resolveCost: 2,
    cooldown: { type: 'per_combat', maxUses: 1 },
    effects: [
      { type: 'heal', value: 3 },
    ],
    keywords: ['heal', 'self'],
    requiresRoll: false,
    generatesThreat: false,
  },

  battle_cry: {
    id: 'battle_cry',
    name: 'Battle Cry',
    description: 'A rallying shout that inspires all allies to fight harder',
    category: 'utility',
    stat: 'spirit',
    targeting: 'all_allies',
    range: 'self',
    apCost: 1,
    resolveCost: 1,
    cooldown: { type: 'per_combat', maxUses: 1 },
    effects: [
      { type: 'apply_condition', condition: 'inspired', duration: 2, target: 'target' },
    ],
    keywords: ['buff', 'allies', 'morale'],
    requiresRoll: false,
    generatesThreat: false,
  },

  nimble_strike: {
    id: 'nimble_strike',
    name: 'Nimble Strike',
    description: 'Dash forward and strike in a single fluid motion',
    category: 'movement',
    stat: 'grace',
    targeting: 'single_enemy',
    range: 'close',
    apCost: 3,
    resolveCost: 1,
    cooldown: { type: 'per_round', maxUses: 1 },
    effects: [
      { type: 'self_move', direction: 'close' },
      { type: 'damage', baseDamage: 2 },
    ],
    keywords: ['melee', 'physical', 'movement'],
    requiresRoll: true,
    generatesThreat: true,
    threatProperties: { canBeBlocked: true, canBeDodged: true, canBeParried: true },
  },

  taunt: {
    id: 'taunt',
    name: 'Taunt',
    description: 'Provoke an enemy into attacking you instead of your allies',
    category: 'utility',
    stat: 'spirit',
    targeting: 'single_enemy',
    range: 'ranged',
    apCost: 1,
    resolveCost: 1,
    cooldown: { type: 'per_round', maxUses: 1 },
    effects: [
      { type: 'apply_condition', condition: 'taunted', duration: 1, target: 'target' },
    ],
    keywords: ['taunt', 'control'],
    requiresRoll: false,
    generatesThreat: false,
  },

  feint: {
    id: 'feint',
    name: 'Feint',
    description: 'A deceptive maneuver that improves your next attack',
    category: 'utility',
    stat: 'grace',
    targeting: 'self',
    range: 'self',
    apCost: 1,
    resolveCost: 1,
    cooldown: { type: 'per_round', maxUses: 1 },
    effects: [
      { type: 'apply_condition', condition: 'inspired', duration: 1, target: 'self' },
    ],
    keywords: ['buff', 'self', 'deception'],
    requiresRoll: false,
    generatesThreat: false,
  },

  disengage: {
    id: 'disengage',
    name: 'Disengage',
    description: 'Carefully retreat from melee without exposing yourself',
    category: 'movement',
    stat: 'grace',
    targeting: 'self',
    range: 'self',
    apCost: 1,
    resolveCost: 1,
    cooldown: { type: 'per_round', maxUses: 1 },
    effects: [
      { type: 'self_move', direction: 'retreat' },
    ],
    keywords: ['movement', 'retreat', 'safe'],
    requiresRoll: false,
    generatesThreat: false,
  },

  rallying_shout: {
    id: 'rallying_shout',
    name: 'Rallying Shout',
    description: 'A powerful cry that restores resolve to all allies',
    category: 'utility',
    stat: 'spirit',
    targeting: 'all_allies',
    range: 'self',
    apCost: 2,
    resolveCost: 2,
    cooldown: { type: 'per_combat', maxUses: 1 },
    effects: [
      { type: 'restore_resolve', value: 2 },
    ],
    keywords: ['support', 'allies', 'resolve'],
    requiresRoll: false,
    generatesThreat: false,
  },
};

// ============================================================================
// RUNTIME CUSTOM SKILLS
// ============================================================================

/** Runtime map for custom skills registered per combat instance */
let runtimeSkills: Record<string, SkillDefinition> = {};

/**
 * Register custom skills (e.g., from DB tags/items) for the current combat.
 * These are checked first when looking up skills, allowing overrides.
 */
export function registerCustomSkills(skills: SkillDefinition[]): void {
  for (const skill of skills) {
    runtimeSkills[skill.id] = skill;
  }
}

/** Clear all runtime custom skills (call between combats) */
export function clearCustomSkills(): void {
  runtimeSkills = {};
}

// ============================================================================
// HELPERS
// ============================================================================

export function getSkill(id: string): SkillDefinition | undefined {
  return runtimeSkills[id] || SKILL_CATALOG[id];
}

/**
 * Resolve skill IDs from a set of NPC tags.
 * Delegates to the unified TAG_COMBAT_EFFECTS catalog via resolveSkills.
 */
export function getSkillsForNpcTags(tags: string[]): string[] {
  return resolveSkills(tags);
}
